import { ChangeEvent, FC, Fragment, useCallback } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { oneTimeReportDetailsSubjectSelector } from "@/store/reports/selector/one-time/oneTimeReportDetailsSubjectSelector";
import {
  setOneTimeReportDetails,
  setReportsData,
} from "@/store/reports/reportsSlice";
import { reportsDataEmailSelector } from "@/store/reports/selector/reportsDataEmailSelector";
import { EmailsSelection } from "@/components/common/integration-dialogs/components/email/components/email-selection/EmailsSelection";
import { Emails } from "@/services/cloudchipr.api";
import { Editor } from "@/storybook/editor/Editor";

export const SendReportEmailSection: FC = () => {
  const dispatch = useAppDispatch();
  const subject = useAppSelector(oneTimeReportDetailsSubjectSelector);
  const emails = useAppSelector(reportsDataEmailSelector);

  const subjectChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        setOneTimeReportDetails({
          subject: e.target.value,
        }),
      );
    },
    [dispatch],
  );

  const emailsChangeHandler = useCallback(
    (newEmails: Emails) => {
      dispatch(
        setReportsData({
          emails: {
            custom_message: emails?.custom_message ?? "",
            ...newEmails,
          },
        }),
      );
    },
    [dispatch, emails],
  );

  const customMessageChangeHandler = useCallback(
    (html: string) => {
      dispatch(
        setReportsData({
          emails: {
            ...emails,
            to: emails?.to ?? [],
            custom_message: html,
          },
        }),
      );
    },
    [emails, dispatch],
  );

  return (
    <Fragment>
      <Stack spacing={3}>
        <EmailsSelection
          toEmails={emails?.to ?? []}
          ccEmails={emails?.cc}
          bccEmails={emails?.bcc}
          onEmailsChange={emailsChangeHandler}
        />

        <TextField
          fullWidth
          size="small"
          label="Subject"
          onChange={subjectChangeHandler}
          name="body.message.subject"
          value={subject}
          error={!subject}
          helperText={!subject ? "Subject is required" : ""}
        />
      </Stack>

      <Box flexGrow={1}>
        <Typography variant="body1" fontWeight="bold" mb={1}>
          Message
        </Typography>

        <Box bgcolor="white" border={1} borderColor="grey.300" borderRadius={2}>
          <Editor
            border={false}
            minHeight={160}
            content={emails?.custom_message}
            placeholder="Write a message..."
            onChange={customMessageChangeHandler}
            commands={[["bold", "italic", "strike"], "link"]}
          />
        </Box>
      </Box>
    </Fragment>
  );
};
